import { useAuth } from "@/auth/auth.ts";
import type { components } from "@/client/backend/zz_schema";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";

export type APIKey = components["schemas"]["APIKey"];
export type APIKeyCreate = components["schemas"]["APIKeyCreate"];

// The lifetimes every credential offers, API keys and tunnel tokens alike.
export const keyLifetimeOptions = [
  { months: "3", label: "3 months" },
  { months: "12", label: "1 year" },
  { months: "60", label: "5 years" },
];

export function useOrganizationAPIKeys(organizationId: string | undefined): {
  tokens: APIKey[] | "loading";
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any;
} {
  const auth = useAuth();
  const { data, error } = useSWR(
    organizationId ? `/v1/organizations/${organizationId}/api_keys` : null,
    () => {
      if (!organizationId) return undefined;
      return auth.client.GET("/v1/organizations/{organizationId}/api_keys", {
        params: {
          path: {
            organizationId,
          },
        },
      });
    }
  );

  return {
    tokens: data?.data ?? "loading",
    error,
  };
}

export function useCreateAPIKey(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId === "loading" ? null : `/v1/organizations/${organizationId}/api_keys`,
    async (_key: string, { arg: tokenCreate }: { arg: APIKeyCreate }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.POST("/v1/organizations/{organizationId}/api_keys", {
        params: {
          path: {
            organizationId,
          },
        },
        body: tokenCreate,
      });
    }
  );
}

export function useDeleteAPIKey(organizationId: string | "loading") {
  const auth = useAuth();

  return useSWRMutation(
    organizationId === "loading" ? null : `/v1/organizations/${organizationId}/api_keys`,
    async (_key: string, { arg: apiKeyId }: { arg: string }) => {
      if (organizationId === "loading") return undefined;
      return auth.client.DELETE("/v1/organizations/{organizationId}/api_keys/{apiKeyId}", {
        params: {
          path: {
            organizationId,
            apiKeyId,
          },
        },
      });
    }
  );
}
